import { useCallback, useContext, useEffect, useState } from "react";
import {
    createExperience, deleteExperience, duplicateExperience, getExperiences,
    markExperienceAsDeleted, markExperienceAsDraft, markExperienceAsPublic,
} from "../utils/ViewModel";
import { PageStandard } from "../utils/PageStandard";
import { Experience, ExperienceStatus, ExperienceType } from "../utils/Model";
import {
    Box, Button, Chip, CircularProgress, Divider, Dropdown, IconButton,
    Input, Menu, MenuButton, MenuItem, Sheet, Stack, Tabs, TabList, Tab, Tooltip, Typography,
} from "@mui/joy";
import { useNavigate } from "react-router-dom";
import {
    Add, CardGiftcard, ContentCopy, DeleteForever, DeleteOutline, Drafts,
    Group, LocationOn, Mail, Monitor, MoreVert, Notifications,
    PlayCircleFilled, Publish, Search, TaskAlt, Translate,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ListDivider from "@mui/joy/ListDivider";
import { FeedbackContext } from "../utils/FeedbackContext";
import { formatDateTimeShort, getUserTimezone } from "../utils/Utils";

type StatusFilter = "all" | ExperienceStatus;

export function Experiences() {
    const { setMessage, setError } = useContext(FeedbackContext);
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const navigate = useNavigate();

    const refresh = useCallback(async () => {
        const data = await getExperiences();
        setExperiences(data);
    }, []);

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, [refresh]);

    const handleNew = async () => {
        setCreating(true);
        const id = await createExperience();
        setCreating(false);
        if (id) navigate(`/game/${id}`);
    };

    const filtered = experiences.filter((e) => {
        if (statusFilter !== "all" && e.status !== statusFilter) return false;
        if (!search.trim()) return true;
        return e.title.toLowerCase().includes(search.toLowerCase()) ||
            (e.location_reference ?? "").toLowerCase().includes(search.toLowerCase());
    });

    const counts = {
        all: experiences.length,
        [ExperienceStatus.public]: experiences.filter((e) => e.status === ExperienceStatus.public).length,
        [ExperienceStatus.draft]: experiences.filter((e) => e.status === ExperienceStatus.draft).length,
        [ExperienceStatus.deleted]: experiences.filter((e) => e.status === ExperienceStatus.deleted).length,
    };

    return (
        <PageStandard
            title="Esperienze di gioco"
            sidebarVisible={true}
            headerEnd={
                <Button startDecorator={<Add />} onClick={handleNew} loading={creating}>
                    Crea nuova
                </Button>
            }
        >
            {/* Toolbar */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Input
                    placeholder="Cerca per titolo o luogo…"
                    startDecorator={<Search />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, maxWidth: 380 }}
                />
                <Typography level="body-sm" color="neutral" sx={{ whiteSpace: "nowrap" }}>
                    {filtered.length} / {experiences.length}
                </Typography>
            </Stack>

            {/* Status tabs */}
            <Tabs
                value={statusFilter}
                onChange={(_, v) => setStatusFilter(v as StatusFilter)}
                sx={{ mb: 2.5, bgcolor: "transparent" }}
            >
                <TabList>
                    <Tab value="all">Tutte <Chip size="sm" sx={{ ml: 0.5 }}>{counts.all}</Chip></Tab>
                    <Tab value={ExperienceStatus.public}>Pubbliche <Chip size="sm" color="success" sx={{ ml: 0.5 }}>{counts[ExperienceStatus.public]}</Chip></Tab>
                    <Tab value={ExperienceStatus.draft}>Bozze <Chip size="sm" color="primary" sx={{ ml: 0.5 }}>{counts[ExperienceStatus.draft]}</Chip></Tab>
                    <Tab value={ExperienceStatus.deleted}>Eliminate <Chip size="sm" color="danger" sx={{ ml: 0.5 }}>{counts[ExperienceStatus.deleted]}</Chip></Tab>
                </TabList>
            </Tabs>

            {/* Content */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 10 }}>
                    <Typography level="title-md" color="neutral">Nessuna esperienza trovata</Typography>
                </Box>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}>
                    {filtered.map((exp) => (
                        <ExperienceCard
                            key={exp.id}
                            exp={exp}
                            onRefresh={refresh}
                            onMessage={setMessage}
                            onError={setError}
                        />
                    ))}
                </Box>
            )}
        </PageStandard>
    );
}

// ── Card ──────────────────────────────────────────────────────────────────────

interface CardProps {
    exp: Experience;
    onRefresh: () => Promise<void>;
    onMessage: (msg: string) => void;
    onError: (err: string) => void;
}

function ExperienceCard({ exp, onRefresh, onMessage, onError }: CardProps) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const act = async (fn: () => Promise<any>, successMsg?: string) => {
        setBusy(true);
        try {
            await fn();
            if (successMsg) onMessage(successMsg);
            await onRefresh();
        } catch (e: any) {
            onError(e.message || "Errore");
        } finally {
            setBusy(false);
        }
    };

    const handleDuplicate = () =>
        act(() => duplicateExperience(exp.id), `"${exp.title}" duplicata`);

    const handleDelete = () => {
        if (!window.confirm(`Eliminare definitivamente "${exp.title}"? Questa azione è irreversibile.`)) return;
        act(() => deleteExperience(exp.id), `"${exp.title}" eliminata definitivamente`);
    };

    const statusColor = exp.status === ExperienceStatus.public ? "success"
        : exp.status === ExperienceStatus.draft ? "primary" : "danger";

    const statusLabel = exp.status === ExperienceStatus.public ? "Pubblica"
        : exp.status === ExperienceStatus.draft ? "Bozza" : "Eliminata";

    const isEvent = exp.type === ExperienceType.event;

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: "lg",
                p: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                opacity: busy ? 0.6 : 1,
                transition: "opacity 0.15s",
            }}
        >
            {/* Cover image */}
            {exp.img_url ? (
                <Box
                    component="img"
                    src={exp.img_url}
                    alt=""
                    sx={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                    onError={(e: any) => { e.target.style.display = "none"; }}
                />
            ) : (
                <Box
                    sx={{
                        height: 120, display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: "background.level2",
                        fontSize: exp.emoji ? 48 : 32,
                    }}
                >
                    {exp.emoji || "🗺️"}
                </Box>
            )}

            <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Badges */}
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                    <Chip size="sm" variant="solid" color={statusColor}>{statusLabel}</Chip>
                    <Chip size="sm" variant="outlined" color="neutral">
                        {isEvent ? "Evento" : "Permanente"}
                    </Chip>
                </Stack>

                {/* Title */}
                <Typography
                    level="title-md"
                    fontWeight="bold"
                    sx={{ cursor: "pointer", "&:hover": { color: "primary.500" } }}
                    onClick={() => navigate(`/game/${exp.id}`)}
                >
                    {exp.title || <Typography color="neutral" fontStyle="italic">Senza titolo</Typography>}
                </Typography>

                {/* Meta */}
                {exp.location_reference && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocationOn sx={{ fontSize: 14, color: "text.tertiary" }} />
                        <Typography level="body-xs" color="neutral">{exp.location_reference}</Typography>
                    </Stack>
                )}
                {isEvent && exp.start_datetime && (
                    <Typography level="body-xs" color="neutral">
                        📅 {formatDateTimeShort(exp.start_datetime)}
                        {exp.end_datetime ? ` → ${formatDateTimeShort(exp.end_datetime)}` : ""}
                        {` (${getUserTimezone()})`}
                    </Typography>
                )}

                <Typography level="body-xs" color="neutral" fontFamily="monospace" sx={{ mt: "auto", pt: 0.5 }}>
                    {exp.id}
                </Typography>
            </Box>

            <Divider />

            {/* Actions bar */}
            <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.75, gap: 0.5 }}>
                <Tooltip title="Modifica">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/${exp.id}`)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Flow di gioco">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/flow/${exp.id}`)}>
                        <PlayCircleFilled fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Partecipanti">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/players/${exp.id}`)}>
                        <Group fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Monitor live">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/monitor/${exp.id}`)}>
                        <Monitor fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Task avanzati">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/tasks/${exp.id}`)}>
                        <TaskAlt fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Premi e sticker">
                    <IconButton size="sm" variant="plain" onClick={() => navigate(`/game/rewards/${exp.id}`)}>
                        <CardGiftcard fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flex: 1 }} />

                {/* Context menu */}
                <Dropdown>
                    <MenuButton size="sm" variant="plain" slots={{ root: IconButton }}>
                        <MoreVert fontSize="small" />
                    </MenuButton>
                    <Menu size="sm" placement="bottom-end">
                        <MenuItem onClick={handleDuplicate} disabled={busy}>
                            <ContentCopy fontSize="small" />
                            Duplica esperienza
                        </MenuItem>

                        <ListDivider />

                        <MenuItem onClick={() => navigate(`/game/sendNotifications/${exp.id}`)}>
                            <Notifications fontSize="small" />
                            Push notifications
                        </MenuItem>
                        <MenuItem onClick={() => navigate(`/game/sendEmail/${exp.id}`)}>
                            <Mail fontSize="small" />
                            Email
                        </MenuItem>
                        <MenuItem onClick={() => navigate(`/game/translations/${exp.id}`)}>
                            <Translate fontSize="small" />
                            Traduzioni
                        </MenuItem>

                        <ListDivider />

                        {exp.status !== ExperienceStatus.public && (
                            <MenuItem color="success" onClick={() => act(() => markExperienceAsPublic(exp.id), `"${exp.title}" segnata come pubblica`)}>
                                <Publish fontSize="small" />
                                Segna come pubblica
                            </MenuItem>
                        )}
                        {exp.status !== ExperienceStatus.draft && (
                            <MenuItem color="neutral" onClick={() => act(() => markExperienceAsDraft(exp.id), `"${exp.title}" segnata come bozza`)}>
                                <Drafts fontSize="small" />
                                Segna come bozza
                            </MenuItem>
                        )}
                        {exp.status !== ExperienceStatus.deleted && (
                            <MenuItem color="warning" onClick={() => act(() => markExperienceAsDeleted(exp.id), `"${exp.title}" segnata come eliminata`)}>
                                <DeleteOutline fontSize="small" />
                                Segna come eliminata
                            </MenuItem>
                        )}

                        <ListDivider />

                        <MenuItem color="danger" onClick={handleDelete}>
                            <DeleteForever fontSize="small" />
                            Elimina definitivamente
                        </MenuItem>
                    </Menu>
                </Dropdown>
            </Stack>
        </Sheet>
    );
}
